import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePDF, PDFDownloadLink } from '@react-pdf/renderer';
import { isMobile } from 'react-device-detect';
import Invoice from '../components/reports/Invoice';

const API_BASE_URL = 'https://link.fslife.in';

// Desktop viewer. usePDF renders the document to a blob URL once, and we only
// mount the <iframe> after that URL exists. <PDFViewer> instead points its iframe
// at the blob *while* it renders, so on the first (cold-cache) load the async
// render can finish out of sync with the iframe and leave it blank until a
// reload. Gating on `instance.url` removes that race and shows a spinner until
// the PDF is genuinely ready.
function InvoiceViewer({ invoice }) {
  const [instance] = usePDF({ document: <Invoice invoice={invoice} /> });

  if (instance.error) {
    return (
      <div className="center-screen">
        <h1 className="message-title">Couldn’t generate the PDF</h1>
        <p className="message-text">Please reload the page and try again.</p>
      </div>
    );
  }

  if (instance.loading || !instance.url) {
    return (
      <div className="center-screen">
        <div className="spinner" aria-hidden="true" />
        <p className="message-text">Preparing invoice…</p>
      </div>
    );
  }

  return (
    <iframe
      title="Invoice"
      src={instance.url}
      style={{ border: 'none', display: 'block', width: '100%', height: '100vh' }}
    />
  );
}

export default function FsStore() {
  const { orderId, uuid } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'ready'

  useEffect(() => {
    let active = true;

    // Simple GET call to the hardcoded base URL; no X-API-Key header.
    async function getInvoice() {
      try {
        const response = await fetch(
          `${API_BASE_URL}/v0/api/invoice/${orderId}/${uuid}`
        );
        const data = await response.json();
        // API envelope is { success, data }. Only a successful, non-empty
        // invoice is usable — e.g. { success:false, data:{} } is not.
        return data && data.success ? data.data : null;
      } catch (error) {
        console.log(error);
        return null;
      }
    }

    getInvoice().then((invoiceData) => {
      if (!active) return;
      // Show an error (not an infinite spinner) for success:false, empty
      // data:{}, or a payload with no items. The items guard also stops
      // Invoice.js from crashing on `invoice.items.length`.
      if (invoiceData && Array.isArray(invoiceData.items)) {
        setInvoice(invoiceData);
        setStatus('ready');
      } else {
        setStatus('error');
      }
    });

    return () => {
      active = false;
    };
  }, [orderId, uuid]);

  if (status === 'loading') {
    return (
      <div className="center-screen">
        <div className="spinner" aria-hidden="true" />
        <p className="message-text">Loading invoice…</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="center-screen">
        <h1 className="message-title">Invoice not available</h1>
        <p className="message-text">
          We couldn’t load an invoice for this link. Please check the URL or try again later.
        </p>
      </div>
    );
  }

  return isMobile ? (
    <PDFDownloadLink
      document={<Invoice invoice={invoice} />}
      fileName="fablestreet_invoice.pdf"
      style={{ color: 'black' }}
    >
      {({ loading }) =>
        loading ? (
          'Loading document...'
        ) : (
          <button
            style={{
              margin: '5px',
              color: 'black',
              background: 'transparent',
              border: '1px solid black',
              borderRadius: '4px',
              width: '50vw',
              position: 'relative',
              top: '40vh',
              left: '20vw',
              padding: '8px',
              cursor: 'pointer',
            }}
          >
            <strong>Download Invoice</strong>
          </button>
        )
      }
    </PDFDownloadLink>
  ) : (
    <InvoiceViewer invoice={invoice} />
  );
}
